import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Calendar, User, ArrowRight, Edit } from '../../lib/lucide-proxy';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import { supabase } from '../../config/supabase';
import SEO from '../../components/seo/SEO'; // New SEO Component

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { hasAnyRole } = useAuth();
  const navigate = useNavigate();

  const canEdit = hasAnyRole(['admin', 'brewery_owner']);

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, []);

  const fetchPosts = async () => {
    try {
      // Only fetch necessary fields for the list to optimize performance
      const { data, error } = await supabase
        .from('blog_posts')
        .select('id, title, slug, excerpt, featured_image, created_at, author, category:blog_categories(name)')
        .eq('is_published', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_categories')
        .select('*');
      
      if (error) throw error;
      if (data && data.length > 0) {
        setCategories(['Todos', ...data.map(c => c.name)]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'Todos' || post.category?.name === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <>
      <SEO 
        title="Blog Cervejeiro" 
        description="Descubra tudo sobre o universo da cerveja artesanal. Dicas, harmonizações, novidades e guias completos."
        keywords="blog cerveja, cerveja artesanal, dicas de cerveja, harmonização, ipa, stout"
      />

      <div className="bg-cream-300 min-h-screen pb-20">
        <section className="bg-dark-900 text-cream-300 py-20 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute -right-20 -top-20 w-96 h-96 bg-primary-500 rounded-full blur-3xl" />
            <div className="absolute -left-20 -bottom-20 w-96 h-96 bg-copper-500 rounded-full blur-3xl" />
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <h1 className="font-display font-bold text-4xl sm:text-5xl mb-6">
              Blog Cervejeiro
            </h1>
            <p className="text-xl text-cream-400 max-w-2xl mx-auto mb-10">
              Explore nosso conteúdo sobre cultura cervejeira, dicas de harmonização e novidades do mercado.
            </p>
            
            <div className="max-w-xl mx-auto relative mb-8">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-dark-500 w-5 h-5" />
              <input 
                type="text" 
                placeholder="Buscar artigos..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-xl bg-white text-dark-900 placeholder-dark-500 focus:ring-2 focus:ring-primary-500 focus:outline-none shadow-lg"
              />
            </div>

            {canEdit && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex justify-center"
              >
                <Button 
                  variant="primary" 
                  onClick={() => navigate('/admin/blog')}
                  className="flex items-center space-x-2 bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all"
                >
                  <Edit className="w-4 h-4" />
                  <span>Gerenciar Blog / Novo Post</span>
                </Button>
              </motion.div>
            )}
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-wrap gap-3 mb-12 justify-center">
            {categories.map(category => (
              <button 
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeCategory === category 
                    ? 'bg-primary-500 text-white shadow-md' 
                    : 'bg-white text-dark-600 hover:bg-primary-50'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, index) => (
              <motion.article 
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-medium transition-all duration-300 group h-full flex flex-col"
              >
                <Link to={`/blog/${post.slug}`} className="flex-1 flex flex-col">
                  {/* IMAGE STANDARDIZATION: aspect-video (16:9) + object-cover */}
                  <div className="relative w-full aspect-video overflow-hidden bg-gray-100">
                    <img 
                      src={post.featured_image || 'https://images.unsplash.com/photo-1526947425960-945c6e72858f?auto=format&fit=crop&w=800&q=80'} 
                      alt={post.title} 
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                    {post.category && (
                      <div className="absolute top-4 left-4">
                        <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-primary-600 uppercase tracking-wider shadow-sm">
                          {post.category.name}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center text-sm text-dark-500 space-x-4 mb-4">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(post.created_at).toLocaleDateString('pt-BR')}</span>
                      </div>
                      {post.author && (
                        <div className="flex items-center space-x-1">
                          <User className="w-4 h-4" />
                          <span>{post.author}</span>
                        </div>
                      )}
                    </div>
                    <h3 className="font-display font-bold text-xl text-dark-900 mb-3 group-hover:text-primary-500 transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-dark-600 mb-6 line-clamp-3 flex-1">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center text-primary-500 font-medium group-hover:text-primary-600 mt-auto">
                      Leia mais <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>

          {!loading && filteredPosts.length === 0 && (
            <div className="text-center py-20">
              <div className="bg-white rounded-2xl p-8 max-w-md mx-auto shadow-sm">
                <p className="text-xl text-dark-500 mb-2">Nenhum artigo encontrado.</p>
                <p className="text-dark-400">Tente buscar por outro termo ou categoria.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Blog;